import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ErrorMessage, ICustomErrorActionType } from 'components/Notification/ErrorMessage';
import InfoMessage from 'components/Notification/InfoMessage';
import WarningMessage from 'components/Notification/WarningMessage';
import { LockedUntilStartMessage } from 'components/Tasks/LockedUntilStartMessage';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import Task, { TaskWorkflowStatus } from 'models/tasks/task';
import { Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AppError from 'utils/appError';

interface ITaskModalMessagesProps {
  task: Task;
  error?: AppError;
  onAfterErrorAction: (actionType: ICustomErrorActionType) => Promise<void>;
  //the template of this new task has connected webhooks (which trigger a workflow)
  newEventHasWorkflows: boolean;
  onShowWorkflowDetails: () => void;
  onShowContext: () => void;
}

// The messages of a normal task dialog under the header: the last error, the workflow state, the pending
// follow-up, the lock until the start date.
export const TaskModalMessages = (props: ITaskModalMessagesProps) => {
  const { t } = useTranslation(['task']);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const { task } = props;

  const getWorkflowMessage = (): JSX.Element | null => {
    if (appContext.isMobileView) return null;
    if (task.recurringPattern.isActive) {
      //normal task series are recurring and a workflow cannot be started for recurring tasks
      return null;
    }
    if (task.taskId === -1 && props.newEventHasWorkflows) {
      return <InfoMessage message={t('task:Workflows.Messages.TaskStart')} />;
    }
    if (task.webhookStatus === TaskWorkflowStatus.Success) {
      return (
        <InfoMessage>
          <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
            <IPText variant="small"> {t('task:Workflows.Messages.TaskSuccess')} </IPText>
            <IPLink onClick={props.onShowWorkflowDetails}>{t('task:Workflows.Messages.DetailLink')}</IPLink>
          </IPStack>
        </InfoMessage>
      );
    }
    if (task.webhookStatus === TaskWorkflowStatus.Failure) {
      return (
        <WarningMessage>
          <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
            <IPText styles={{ root: { color: theme.semanticColors.messageText } }} variant="small">
              {t('task:Workflows.Messages.TaskFailure')}
            </IPText>
            <IPLink onClick={props.onShowWorkflowDetails}>{t('task:Workflows.Messages.DetailLink')}</IPLink>
          </IPStack>
        </WarningMessage>
      );
    }

    return null;
  };

  const getFollowUpMessage = (): JSX.Element | null => {
    if (!task.hasUnlinkedFollowUp) return null;

    return (
      <InfoMessage>
        <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
          <IPText variant="small">{t('task:FollowUp.PendingMessage')}</IPText>
          <IPLink onClick={props.onShowContext}>{t('task:FollowUp.CreateNow')}</IPLink>
        </IPStack>
      </InfoMessage>
    );
  };

  return (
    <Fragment>
      <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
      {getWorkflowMessage()}
      {getFollowUpMessage()}
      <LockedUntilStartMessage task={task} />
    </Fragment>
  );
};
